// Review Client Application — the staff read of one submission (SEP-263.25,
// slice 8).
//
// # THE SCREEN IS READ-ONLY, AND IT IS READ-ONLY STRUCTURALLY
//
// There is ONE route here and it is a GET. The snapshot is append-only
// (`ClientApplicationSubmission`), so there is no Action that rewrites an
// answer and there is no endpoint that could carry one. A correction is a new
// row beside the original, and slice 9 builds it.
//
// # PART 11 IS NOT IN THIS SHAPE
//
// The four portal security answers are bank-grade identifiers. The snapshot
// holds them in a separate map (`securityAnswers`) that this projection never
// reads, and `clientApplicationReview(answers, 'staff')` drops any question
// that carries `excludeFromStaffReview`. A staff reader therefore cannot print
// a security answer, because the answer is not in the value they read.
//
// # THE DUPLICATE FLAGS ARE THE POINT OF THE SCREEN (decision D10)
//
// An online application NEVER links to an existing client and NEVER replaces a
// portal password. The match still runs at submit, and what it finds lands on
// the case as `possibleDuplicates`. It travels in this shape because it is the
// duplicate check AND the impersonation check, and merging is the decision of
// the investment manager.
//
// # THE FACTS, AND NO VERDICT
//
// `keyFacts`, `verification` and `timeline` give the manager what the platform
// KNOWS. There is no score, no risk band and no recommendation: the firm
// decided the manager is the verifier, so the projection states facts and the
// manager decides.

import { initContract } from '@ts-rest/core';
import { z } from 'zod';

import { CLIENT_APPLICATION_FORM_IDS } from '../client-application-form-ids';
import { clientApplicationServiceTypeSchema } from './client-application';
import { onboardingPossibleDuplicateSchema } from './onboarding';

const c = initContract();

const errorBody = z.object({ error: z.string(), code: z.string().optional() });

/** One answered question of the staff review. */
export const staffApplicationAnswerSchema = z.object({
    questionId: z.string(),
    label: z.string(),
    /** The sub-heading of the form, where the question carries one. */
    group: z.string().nullable(),
    /** The answer as text. A money answer already carries the pound sign. */
    display: z.string(),
    /** True when the question is required and the client left it blank. */
    missing: z.boolean(),
});
export type StaffApplicationAnswer = z.infer<typeof staffApplicationAnswerSchema>;

/** One section of the form, in the order the form prints it. */
export const staffApplicationSectionSchema = z.object({
    sectionId: z.string(),
    /** The part number of the paper form, or null for the service-type block. */
    part: z.number().nullable(),
    title: z.string(),
    answers: z.array(staffApplicationAnswerSchema),
    complete: z.boolean(),
});
export type StaffApplicationSection = z.infer<typeof staffApplicationSectionSchema>;

/** One answer of the band the manager reads first. */
export const staffApplicationKeyFactSchema = z.object({
    questionId: z.string(),
    label: z.string(),
    display: z.string(),
    /** True for a money or a count answer, which a surface sets in figures. */
    numeric: z.boolean(),
});
export type StaffApplicationKeyFact = z.infer<typeof staffApplicationKeyFactSchema>;

/**
 * The four states a member of staff reads for an identity check (SEP-278).
 *
 * - `outstanding`: no check runs. The check has not started, or the vendor
 *   closed it with no verdict.
 * - `in_progress`: the client holds the link, or the vendor reviews the check.
 * - `passed`: the check returned Complete. The sandbox word counts.
 * - `failed`: the vendor rejected the check.
 */
export const staffIdentityCheckStateSchema = z.enum([
    'outstanding',
    'in_progress',
    'passed',
    'failed',
]);
export type StaffIdentityCheckState = z.infer<typeof staffIdentityCheckStateSchema>;

/** The state of one status word. A word the table does not hold is outstanding. */
export function staffIdentityCheckState(status: string | null): StaffIdentityCheckState {
    switch (status) {
        case 'complete':
        case 'fixture':
            return 'passed';
        case 'rejected':
            return 'failed';
        case 'in_progress':
        case 'pending_approval':
            return 'in_progress';
        default:
            return 'outstanding';
    }
}

/**
 * What the identity check returned, and when (SEP-263.45 to .47).
 *
 * It is the POINTER and the VERDICT. There is no image, no selfie and no
 * extracted document field in this shape, because there is none in the
 * snapshot: the documents stay with the vendor.
 *
 * THE LATEST STATE WINS (SEP-278). The client submits with no identity check,
 * and each state of the check is an `IdentityCheck` row. The controller gives
 * the latest row. With no row, it gives the check fields of the snapshot.
 * `source` says which one the reader sees.
 */
export const staffApplicationVerificationSchema = z.object({
    /** The name of the provider, for example `credas`. */
    provider: z.string().nullable(),
    /** The vendor entity id. A pointer a reader cannot turn into a document. */
    entityId: z.string().nullable(),
    /** The vendor process id, on the same terms. */
    processId: z.string().nullable(),
    /** The verdict word the vendor gave, for example `complete`. */
    status: z.string().nullable(),
    /** The instant the check returned its verdict. */
    verifiedAt: z.string().nullable(),
    /** The state of `status`, in the four words that staff read. */
    state: staffIdentityCheckStateSchema,
    /** `submission` is the snapshot value. `identity_check` is an `IdentityCheck` row. */
    source: z.enum(['submission', 'identity_check']),
    /** The instant the firm saw the state. The submission instant for the snapshot. */
    checkedAt: z.string(),
});
export type StaffApplicationVerification = z.infer<typeof staffApplicationVerificationSchema>;

/**
 * How the client worked through the form.
 *
 * Three facts, and no inference: when they started, when they submitted and
 * how many sittings they took. A manager reads them as circumstantial
 * evidence; the platform draws no conclusion from them.
 *
 * It is null when the draft row is gone, because a draft expires and the
 * snapshot outlives it.
 */
export const staffApplicationTimelineSchema = z.object({
    startedAt: z.string(),
    submittedAt: z.string(),
    /** Whole minutes between the two instants above. */
    elapsedMinutes: z.number(),
    /** How many times the client signed in to work on the form. */
    sittings: z.number(),
});
export type StaffApplicationTimeline = z.infer<typeof staffApplicationTimelineSchema>;

/**
 * The ISA declaration, prefilled from the answers (SEP-280).
 *
 * It is present only when the client chose an ISA. It holds the FACTS the
 * declaration needs, and no wording: the declaration is the document of the
 * firm. A fact the client did not give is an empty string or null.
 */
export const staffApplicationIsaDeclarationSchema = z.object({
    title: z.string(),
    forenames: z.string(),
    surname: z.string(),
    dateOfBirth: z.string(),
    nationalClientIdentifier: z.string(),
    /** The current address of the client, as one line. */
    address: z.string(),
    email: z.string(),
    phone: z.string(),
    accountTypes: z.array(z.string()),
    subscribedThisYear: z.enum(['Yes', 'No']).nullable(),
    /** The amount already subscribed this tax year, formatted, or null. */
    subscribedAmount: z.string().nullable(),
    /** "What is your initial subscription amount?", or null when unanswered. */
    initialSubscription: z.enum(['Full annual ISA allowance', 'Other amount']).nullable(),
    /** The amount of an other amount subscription, formatted, or null. */
    initialSubscriptionAmount: z.string().nullable(),
    /** "What is the source of the subscription?", or null when unanswered. */
    subscriptionSource: z
        .enum(['Cash', 'Transfer from an existing stocks & shares ISA'])
        .nullable(),
});
export type StaffApplicationIsaDeclaration = z.infer<typeof staffApplicationIsaDeclarationSchema>;

/**
 * One person that the identity check must cover (SEP-293, Phase A).
 *
 * The applicant comes first. The identity check runs for the applicant only
 * in Phase A, so the review lists each other person for the investment
 * manager. A person who appears twice in the form (the representative is also
 * a director) is one entry with two roles.
 */
export const staffApplicationPartySchema = z.object({
    partyKey: z.string(),
    roles: z.array(z.enum(['applicant', 'stakeholder', 'trust_party', 'scheme_member'])),
    /** The capacity that the person gave, for example "Director", or an empty string. */
    capacity: z.string(),
    fullName: z.string(),
    /** An ISO calendar day, or an empty string. */
    dateOfBirth: z.string(),
    email: z.string(),
    /**
     * Where the identity check of the person stands (SEP-293, Phase B). The
     * applicant reads the check of the application. Each other person has a
     * check of their own.
     */
    identityState: staffIdentityCheckStateSchema,
});
export type StaffApplicationParty = z.infer<typeof staffApplicationPartySchema>;

/** One submitted application, as a member of staff reads it. */
export const staffClientApplicationSchema = z.object({
    submissionId: z.string(),
    /** The draft this snapshot froze. It is the idempotency key of the act. */
    applicationId: z.string(),
    caseId: z.string().nullable(),
    clientId: z.string().nullable(),
    /** Who filled the form in (SEP-263.22). `client` is the client-led route. */
    origin: z.enum(['client']),
    serviceType: clientApplicationServiceTypeSchema,
    /** The manager the CLIENT named (SEP-263.3). It is not the assigned IM. */
    investmentManagerUserId: z.string(),
    investmentManagerName: z.string(),
    /** The instant the client submitted (SEP-263.19). */
    submittedAt: z.string(),
    /** The form that the client filled in (SEP-293). */
    formId: z.enum(CLIENT_APPLICATION_FORM_IDS),
    /** The name of the form, for example "Corporate account (Execution Only)". */
    formTitle: z.string(),
    /**
     * The version of the form that the client filled in, in the version line
     * of the form (SEP-293.3). A snapshot from before the version field reads
     * as `2.0` of the individual form.
     */
    formVersion: z.string(),
    /** Each person that the identity check must cover, the applicant first (SEP-293). */
    parties: z.array(staffApplicationPartySchema),
    /** The `ComplianceCopyEntry` that governed the wording, or null. */
    complianceCopyEntryId: z.string().nullable(),
    verification: staffApplicationVerificationSchema,
    timeline: staffApplicationTimelineSchema.nullable(),
    /** The account types the client asked for, for example `ISA` (SEP-337). */
    accountTypes: z.array(z.string()),
    keyFacts: z.array(staffApplicationKeyFactSchema),
    sections: z.array(staffApplicationSectionSchema),
    /** The prefilled ISA declaration, or null when the client chose no ISA. */
    isaDeclaration: staffApplicationIsaDeclarationSchema.nullable(),
    /** The possible duplicates the route left for the manager (SEP-263.51). */
    possibleDuplicates: z.array(onboardingPossibleDuplicateSchema),
});
export type StaffClientApplication = z.infer<typeof staffClientApplicationSchema>;

export const staffClientApplicationResponseSchema = z.object({
    ok: z.literal(true),
    application: staffClientApplicationSchema,
});
export type StaffClientApplicationResponse = z.infer<typeof staffClientApplicationResponseSchema>;

/**
 * The path of the filled ISA declaration PDF of one case (SEP-280).
 *
 * The route is a plain GET that streams `application/pdf`, so it has no
 * ts-rest entry: ts-rest carries JSON only. This helper keeps the path in one
 * place for the server and the web app. The route answers 404 when the case
 * has no submitted application in the book of the reader, and 409 when the
 * client chose no ISA.
 */
export function clientApplicationIsaDeclarationPdfPath(caseId: string): string {
    return `/api/onboarding/cases/${encodeURIComponent(caseId)}/client-application/isa-declaration.pdf`;
}

export const clientApplicationStaffContract = c.router(
    {
        getCaseApplication: {
            method: 'GET',
            path: '/onboarding/cases/:caseId/client-application',
            responses: {
                200: staffClientApplicationResponseSchema,
                // 404 = this case has no submitted application, OR the reader's
                // book does not hold it. The two answers are deliberately the
                // same: a desk row scope that named the difference would tell
                // one manager that another manager holds a case (ADR-0005).
                404: errorBody,
            },
            summary: 'What the client submitted, for the investment manager to read',
        },
    },
    {
        pathPrefix: '/api',
        strictStatusCodes: false,
    },
);
